/**
 * Meta (Facebook) Pixel - consent-gated for GDPR compliance.
 * Only loads and fires when isAnalyticsAllowed() is true.
 *
 * Set your Pixel ID in environment: REACT_APP_META_PIXEL_ID
 */

import { isAnalyticsAllowed } from './cookieConsent';
import { CONSENT_LEVELS } from './cookieConsent';

const PIXEL_ID = process.env.REACT_APP_META_PIXEL_ID;
let isInitialized = false;
let initInProgress = false;
let scriptLoaded = false;

/**
 * Define fbq stub so we can queue calls before the script loads.
 */
function ensureFbqStub() {
  if (typeof window === 'undefined') return;
  if (window.fbq) return;
  const n = (window.fbq = function (...args) {
    n.callMethod ? n.callMethod(...args) : n.queue.push(args);
  });
  if (!window._fbq) window._fbq = n;
  n.push = n;
  n.loaded = true;
  n.version = '2.0';
  n.queue = [];
}

/**
 * Load the Meta fbevents.js script.
 */
function loadPixelScript() {
  if (typeof window === 'undefined' || scriptLoaded) return Promise.resolve();
  return new Promise((resolve) => {
    ensureFbqStub();
    const script = document.createElement('script');
    script.async = true;
    script.src = 'https://connect.facebook.net/en_US/fbevents.js';
    script.onload = () => {
      scriptLoaded = true;
      resolve();
    };
    document.head.appendChild(script);
  });
}

/**
 * Initialize the Meta pixel. Only runs when consent is "all" and PIXEL_ID is set.
 */
export async function initMetaPixel() {
  if (typeof window === 'undefined') return;
  if (!PIXEL_ID) {
    console.warn('Meta Pixel: REACT_APP_META_PIXEL_ID is not set');
    return;
  }
  if (!isAnalyticsAllowed()) {
    return;
  }
  if (isInitialized || initInProgress) {
    return;
  }

  initInProgress = true;
  try {
    await loadPixelScript();
    window.fbq('init', PIXEL_ID);
    window.fbq('track', 'PageView');
    isInitialized = true;
    console.log('✅ Meta Pixel initialized with user consent');
  } catch (error) {
    console.warn('Meta Pixel init failed:', error);
  } finally {
    initInProgress = false;
  }
}

/**
 * Track a standard or custom Meta pixel event. No-op if consent not given or pixel not initialized.
 */
export function trackMetaEvent(eventName, parameters = {}) {
  if (typeof window === 'undefined' || !window.fbq) return;
  if (!isAnalyticsAllowed()) return;
  if (!PIXEL_ID) return;

  try {
    if (Object.keys(parameters).length > 0) {
      window.fbq('track', eventName, parameters);
    } else {
      window.fbq('track', eventName);
    }
  } catch (error) {
    console.warn('Meta Pixel track error:', error);
  }
}

/**
 * Track a PageView (e.g. on route change).
 */
export function trackPageView() {
  trackMetaEvent('PageView');
}

/**
 * Track Lead (e.g. contact form submit).
 */
export function trackLead(parameters = {}) {
  trackMetaEvent('Lead', parameters);
}

/**
 * Check if the pixel is initialized (for debugging or conditional UI).
 */
export function isMetaPixelReady() {
  return isInitialized && typeof window !== 'undefined' && !!window.fbq;
}

// Listen for consent changes: init when user accepts "all"
if (typeof window !== 'undefined') {
  window.addEventListener('consentChanged', (event) => {
    const { level } = event.detail || {};
    if (level === CONSENT_LEVELS.ALL && !isInitialized && PIXEL_ID) {
      initMetaPixel();
    }
  });
}
